import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

// sessionStorage 캐시 키 (탭 닫으면 자동 소멸)
const CACHE_KEY = 'gym_auth_cache';

function getCached(userId) {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const cached = JSON.parse(raw);
    // 다른 유저 캐시는 무시
    if (cached.userId !== userId) return null;
    return cached;
  } catch { return null; }
}

function setCache(userId, profile, gym) {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ userId, profile, gym }));
  } catch {}
}

function clearCache() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

function phoneToEmail(phone) {
  return phone.replace(/[^0-9]/g, '') + '@gymapp.local';
}

function trainerPhoneToEmail(phone) {
  return phone.replace(/[^0-9]/g, '') + '@trainer.local';
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [gym, setGym] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDemoMode, setIsDemoMode] = useState(() => localStorage.getItem('gym_demo_mode') === 'true');

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .maybeSingle();
    return data;
  }

  async function fetchGym(userId) {
    const { data } = await supabase
      .from('gyms')
      .select('*')
      .eq('owner_id', userId)
      .maybeSingle();
    return data;
  }

  async function loadUserData(currentSession) {
    if (!currentSession?.user) {
      setProfile(null);
      setGym(null);
      setLoading(false);
      return;
    }
    const userId = currentSession.user.id;

    // 캐시된 데이터가 있으면 즉시 표시 (스피너 없음), 백그라운드에서 최신화
    const cached = getCached(userId);
    if (cached) {
      setProfile(cached.profile);
      setGym(cached.gym);
      setLoading(false);
      // 백그라운드 갱신 (UI 블로킹 없음)
      Promise.all([fetchProfile(userId), fetchGym(userId)]).then(([p, g]) => {
        setProfile(p);
        setGym(g);
        setCache(userId, p, g);
      });
    } else {
      setLoading(true);
      const [profileData, gymData] = await Promise.all([
        fetchProfile(userId),
        fetchGym(userId),
      ]);
      setProfile(profileData);
      setGym(gymData);
      setCache(userId, profileData, gymData);
      setLoading(false);
    }
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      loadUserData(s);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      // TOKEN_REFRESHED는 세션 내용 변경 없음 — 재로드 불필요 (탭 전환 시 새로고침 방지)
      if (event !== 'TOKEN_REFRESHED') {
        loadUserData(s);
      }
    });

    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signUp({ name, email, phone, password, role = 'gym_owner', ...meta }) {
    let authEmail;
    if (role === 'member') authEmail = phoneToEmail(phone);
    else if (role === 'trainer') authEmail = trainerPhoneToEmail(phone);
    else authEmail = email;
    const { data, error } = await supabase.auth.signUp({
      email: authEmail,
      password,
      options: { data: { name, role, ...meta } },
    });
    return { data, error };
  }

  async function signUpTrainer({ gymCode, phone, password, name }) {
    const digits = phone.replace(/[^0-9]/g, '');
    const { data, error } = await supabase.auth.signUp({
      email: trainerPhoneToEmail(digits),
      password,
      options: { data: { name, role: 'trainer' } },
    });
    if (error) return { error };
    const userId = data?.user?.id;
    if (userId) {
      await supabase.rpc('link_trainer_account', {
        p_gym_code: gymCode,
        p_phone: digits,
        p_user_id: userId,
      });
    }
    return { data, error: null };
  }

  async function signUpTrainerViaInvite({ email, password, name, inviteCode }) {
    const { data, error } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: { data: { name, role: 'trainer' } },
    });
    if (error) return { error };
    const userId = data?.user?.id;
    if (userId) {
      const { error: rpcErr } = await supabase.rpc('use_trainer_invite', {
        p_code: inviteCode.trim().toUpperCase(),
        p_user_id: userId,
      });
      if (rpcErr) return { error: rpcErr };
    }
    return { data, error: null };
  }

  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    return { data, error };
  }

  async function findGymByCode(code) {
    const { data, error } = await supabase.rpc('find_gym_by_code', { gym_code: code });
    if (error || !data) return null;
    return data;
  }

  async function lookupMemberByPhone(gymCode, phone) {
    const digits = phone.replace(/[^0-9]/g, '');
    const { data, error } = await supabase.rpc('lookup_member_by_phone', {
      p_gym_code: gymCode,
      p_phone: digits,
    });
    if (error) return null;
    return data;
  }

  async function linkMemberAccount(gymCode, phone) {
    const { data: { user: u } } = await supabase.auth.getUser();
    const digits = phone.replace(/[^0-9]/g, '');
    const { error } = await supabase.rpc('link_member_account', {
      p_gym_code: gymCode,
      p_phone: digits,
      p_user_id: u?.id,
    });
    return { error };
  }

  async function lookupTrainerByPhone(gymCode, phone) {
    const digits = phone.replace(/[^0-9]/g, '');
    const { data, error } = await supabase.rpc('lookup_trainer_by_phone', {
      p_gym_code: gymCode,
      p_phone: digits,
    });
    if (error) return null;
    return data;
  }

  async function registerMemberRequest({ gymCode, name, phone, gender, birthDate, purpose }) {
    const { data: { user: u } } = await supabase.auth.getUser();
    const digits = phone.replace(/[^0-9]/g, '');
    const { error } = await supabase.rpc('register_member_request', {
      p_gym_code: gymCode,
      p_name: name,
      p_phone: digits,
      p_gender: gender || null,
      p_birth_date: birthDate || null,
      p_purpose: purpose || null,
      p_user_id: u?.id,
    });
    return { error: error?.message || null };
  }

  function demoLogin() {
    localStorage.setItem('gym_demo_mode', 'true');
    setIsDemoMode(true);
  }

  async function signOut() {
    localStorage.removeItem('gym_demo_mode');
    setIsDemoMode(false);
    clearCache();
    await supabase.auth.signOut();
  }

  async function refreshGym() {
    if (!user) return;
    const gymData = await fetchGym(user.id);
    setGym(gymData);
    // 캐시 gym만 갱신
    const cached = getCached(user.id);
    if (cached) setCache(user.id, cached.profile, gymData);
  }

  async function resetPasswordForEmail(email) {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin,
    });
    return { error };
  }

  async function updatePassword(newPassword) {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  }

  return (
    <AuthContext.Provider value={{
      session, user, profile, gym, loading, isDemoMode,
      signUp, signUpTrainer, signUpTrainerViaInvite, signIn, signOut, demoLogin, refreshGym,
      findGymByCode, lookupMemberByPhone, linkMemberAccount, registerMemberRequest,
      lookupTrainerByPhone,
      resetPasswordForEmail, updatePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
